import { Checkbox, ListItemButton, ListItemText } from "@mui/material";
import useDebouncedSearchTerm from "hooks/strings/useDebouncedSearchTerm";
import useGetAdditionalCosts from "hooks/organization/useGetAdditionalCosts";
import { useState } from "react";
import { centsToDollarsInput, WorksheetLibrarySelection } from "../worksheetFormValues";
import MultiSelectFooter from "./MultiSelectFooter";
import PickerList from "./PickerList";
import PickerSearch from "./PickerSearch";
import LoadMoreButton from "./LoadMoreButton";
import { PickerProps } from "./types";

export default function AdditionalCostPicker({ onClose, onSelect, onSelectMany }: PickerProps) {
  const { searchTerm, setSearchTerm, debouncedSearchTerm } = useDebouncedSearchTerm("");
  const { data, loading, paginatorInfo, fetchMore } = useGetAdditionalCosts({
    first: 50,
    search: debouncedSearchTerm || undefined,
  });
  const costs = (data ?? []).filter((cost) => cost.feeType === "FLAT");
  const [selected, setSelected] = useState<Record<string, WorksheetLibrarySelection>>({});

  const selectionFor = (cost: (typeof costs)[number]): WorksheetLibrarySelection => ({
    sourceType: "additional_cost",
    sourceId: cost.id,
    sourceName: cost.name,
    name: cost.name,
    rate: centsToDollarsInput(cost.cost ?? 0),
    unit: "EACH",
    quantity: String(cost.feeAmount ?? 1),
  });

  return (
    <PickerList
      title="Select Additional Cost"
      loading={loading}
      empty={costs.length === 0}
      header={
        <PickerSearch
          value={searchTerm}
          placeholder="Search additional costs"
          onChange={setSearchTerm}
        />
      }
      onClose={onClose}
      footer={
        <MultiSelectFooter
          selections={Object.values(selected)}
          onSelectMany={onSelectMany}
          onClose={onClose}
        />
      }
    >
      {costs.map((cost) => {
        const selection = selectionFor(cost);

        return (
          <ListItemButton
            key={cost.id}
            selected={!!selected[cost.id]}
            onClick={() => {
              if (onSelectMany) {
                setSelected((previous) => {
                  const next = { ...previous };
                  if (next[cost.id]) {
                    delete next[cost.id];
                  } else {
                    next[cost.id] = selection;
                  }
                  return next;
                });
                return;
              }
              onSelect(selection);
              onClose();
            }}
          >
            {onSelectMany && <Checkbox checked={!!selected[cost.id]} size="small" />}
            <ListItemText
              primary={cost.name}
              secondary={`Flat fee · ${centsToDollarsInput(cost.cost ?? 0)}`}
            />
          </ListItemButton>
        );
      })}
      {paginatorInfo?.hasMorePages && (
        <LoadMoreButton
          label="Load more additional costs"
          onClick={() =>
            fetchMore({
              page: (paginatorInfo.currentPage ?? 1) + 1,
              updateQuery: (previous, { fetchMoreResult }) => {
                if (!fetchMoreResult?.getAdditionalCosts) {
                  return previous;
                }

                return {
                  ...fetchMoreResult,
                  getAdditionalCosts: {
                    ...fetchMoreResult.getAdditionalCosts,
                    data: [
                      ...(previous.getAdditionalCosts?.data ?? []),
                      ...fetchMoreResult.getAdditionalCosts.data,
                    ],
                  },
                };
              },
            })
          }
        />
      )}
    </PickerList>
  );
}
